import React, { useEffect, useMemo, useState } from "react";
import "./HostelFeeReceipt.css";
import { imgBaseUrl } from "../../api/api";
import { convertNumberToWords } from "../master/Master";
import { formatToDate } from "../master/FormatToDateTime";

export default function HostelFeeReceipt({ data = {} }) {
  const [setting, setSetting] = useState({});
  const [printBothCopies, setPrintBothCopies] = useState(false);

  useEffect(() => {
    try {
      const savedSetting = localStorage.getItem("setting");

      if (savedSetting) {
        setSetting(JSON.parse(savedSetting));
      }
    } catch (error) {
      console.error("Unable to load settings:", error);
      setSetting({});
    }
  }, []);

  /* =========================================================
     DATA
  ========================================================= */

  const studentName = data?.student?.name || data?.student?.student_name || "-";

  const registrationNo = data?.student?.registration_no || "-";

  const fatherName = data?.student?.father_name || "-";

  const mobile = data?.student?.mobile || data?.student?.phone || "-";

  const receiptNo = data?.receipt_no || `REP-${data?.id || "-"}`;

  const paymentDate = data?.payment_date
    ? formatToDate(data.payment_date)
    : "-";

  const paymentMethod = data?.payment_method || "-";

  const transactionNo = data?.transaction_no || "";

  const accountNo = data?.account_no || "";

  const remark = data?.remark || "";

  const amount = Number(data?.amount || 0);

  const modules = Array.isArray(data?.modules) ? data.modules : [];

  /* =========================================================
     COURSE / BATCH / SESSION
  ========================================================= */

  const courseName = useMemo(() => {
    if (modules.some((item) => item?.course_name)) {
      return [
        ...new Set(modules.map((item) => item?.course_name).filter(Boolean)),
      ].join(", ");
    }

    return data?.student?.latest_course_and_fee?.course?.title || "-";
  }, [modules, data]);

  const batchName = useMemo(() => {
    if (modules.some((item) => item?.batch_name)) {
      return [
        ...new Set(modules.map((item) => item?.batch_name).filter(Boolean)),
      ].join(", ");
    }

    return data?.student?.latest_course_and_fee?.batch?.title || "-";
  }, [modules, data]);

  const sessionName = useMemo(() => {
    if (modules.some((item) => item?.session)) {
      return [
        ...new Set(modules.map((item) => item?.session).filter(Boolean)),
      ].join(", ");
    }

    return data?.student?.latest_course_and_fee?.session_year?.session || "-";
  }, [modules, data]);

  /* =========================================================
     LOGOS
  ========================================================= */

  const logoUrl = setting?.logo_img ? imgBaseUrl + setting.logo_img : "";

  const headerLogoUrl = setting?.receipt_header_logo
    ? imgBaseUrl + setting.receipt_header_logo
    : "";

  const signatureUrl =
    setting?.auto_signature === "Yes" && setting?.signature
      ? imgBaseUrl + setting.signature
      : "";

  /* =========================================================
     FORMAT AMOUNT
  ========================================================= */

  const formatAmount = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  /* =========================================================
     RECEIPT COPY
  ========================================================= */

  const ReceiptCopy = ({ copyType }) => {
    return (
      <div className="hostel-receipt-paper">
        {/* TOP BORDER */}
        <div className="hostel-receipt-top-border"></div>

        {/* =====================================================
            HEADER
        ===================================================== */}

        <div className="hostel-receipt-header">
          {headerLogoUrl ? (
            <img
              src={headerLogoUrl}
              alt="Receipt Header"
              className="hostel-receipt-header-image"
            />
          ) : (
            <div className="hostel-receipt-header-content">
              <div className="hostel-receipt-logo">
                {logoUrl ? (
                  <img src={logoUrl} alt="School Logo" />
                ) : (
                  <i className="ri-school-line"></i>
                )}
              </div>

              <div className="hostel-receipt-institute">
                <h1>{setting?.name || "Institution Name"}</h1>

                <div className="hostel-receipt-address">
                  {[
                    setting?.village,
                    setting?.district,
                    setting?.state,
                    setting?.pin_code,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </div>

                <div className="hostel-receipt-contact">
                  {(setting?.mobile || setting?.secondary_mobile) && (
                    <span>
                      <i className="ri-phone-line"></i>

                      {[setting?.mobile, setting?.secondary_mobile]
                        .filter(Boolean)
                        .join("  |  ")}
                    </span>
                  )}

                  {setting?.email && (
                    <span>
                      <i className="ri-mail-line"></i>
                      {setting.email}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* =====================================================
            TITLE BAR
        ===================================================== */}

        <div className="hostel-receipt-title-row">
          <div className="hostel-receipt-title-left">Student Details</div>

          <div className="hostel-receipt-title-main">
            <i className="ri-hotel-bed-line"></i>
            HOSTEL FEE RECEIPT
          </div>

          <div className="hostel-receipt-title-right">{copyType} Copy</div>
        </div>

        {/* =====================================================
            RECEIPT + STUDENT DETAILS
        ===================================================== */}

        <div className="hostel-receipt-details">
          {/* ROW 1 */}

          <div className="receipt-field">
            <span>Receipt No</span>
            <b>{receiptNo}</b>
          </div>

          <div className="receipt-field">
            <span>Date</span>
            <b>{paymentDate}</b>
          </div>

          {/* ROW 2 */}

          <div className="receipt-field">
            <span>Student Name</span>
            <b>{studentName}</b>
          </div>

          <div className="receipt-field">
            <span>Registration No</span>
            <b>{registrationNo}</b>
          </div>

          {/* ROW 3 */}

          <div className="receipt-field">
            <span>Father's Name</span>
            <b>{fatherName}</b>
          </div>

          <div className="receipt-field">
            <span>Mobile No</span>
            <b>{mobile}</b>
          </div>

          {/* ROW 4 */}

          <div className="receipt-field">
            <span>Course Name</span>
            <b>{courseName}</b>
          </div>

          <div className="receipt-field">
            <span>Batch Name</span>
            <b>{batchName}</b>
          </div>
        </div>

        {/* =====================================================
            FEE TABLE
        ===================================================== */}

        <table className="hostel-receipt-table">
          <thead>
            <tr>
              <th className="sr">#</th>
              <th>FEE DESCRIPTION</th>
              <th className="amount">AMOUNT</th>
            </tr>
          </thead>

          <tbody>
            {modules.length > 0 ? (
              modules.map((module, index) => (
                <tr key={index}>
                  <td className="sr">{index + 1}</td>

                  <td>
                    <strong>{module?.type || "Hostel Fee"}</strong>
                  </td>

                  <td className="amount">₹{formatAmount(module?.amount)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="sr">1</td>

                <td>
                  <strong>Hostel Fee</strong>
                </td>

                <td className="amount">₹{formatAmount(amount)}</td>
              </tr>
            )}
          </tbody>

          <tfoot>
            <tr>
              <td colSpan="2">Total</td>

              <td className="amount">₹{formatAmount(amount)}</td>
            </tr>
          </tfoot>
        </table>

        {/* =====================================================
            PAYMENT INFORMATION
        ===================================================== */}

        <div className="hostel-receipt-payment">
          <div className="payment-left">
            <div className="payment-row">
              <span>Amount in Words</span>

              <strong>{convertNumberToWords(amount)} Rupees Only</strong>
            </div>

            <div className="payment-row">
              <span>Payment Method</span>

              <strong>{paymentMethod}</strong>
            </div>

            {accountNo && (
              <div className="payment-row">
                <span>Account No</span>

                <strong>{accountNo}</strong>
              </div>
            )}

            {transactionNo && (
              <div className="payment-row">
                <span>Transaction No</span>

                <strong>{transactionNo}</strong>
              </div>
            )}

            <div className="payment-row">
              <span>Session</span>

              <strong>{sessionName}</strong>
            </div>
          </div>

          {/* SIGNATURE */}

          <div className="hostel-receipt-signature">
            {signatureUrl ? (
              <img src={signatureUrl} alt="Authorized Signature" />
            ) : (
              <div className="signature-empty"></div>
            )}

            <div className="signature-line"></div>

            <strong>Auth. Signature</strong>
          </div>
        </div>

        {/* =====================================================
            REMARK
        ===================================================== */}

        {remark && (
          <div className="hostel-receipt-remark">
            <strong>Remark:</strong>

            <span>{remark}</span>
          </div>
        )}

        {/* =====================================================
            FOOTER
        ===================================================== */}

        <div className="hostel-receipt-footer">
          <span>Fees once paid are non-refundable.</span>

          <span>Receipt ID: {data?.id || "-"}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="hostel-receipt-container">
      {/* =====================================================
          OPTIONS
      ===================================================== */}

      <div className="hostel-receipt-options no-print">
        <label className="hostel-copy-checkbox">
          <input
            type="checkbox"
            checked={printBothCopies}
            onChange={(e) => setPrintBothCopies(e.target.checked)}
          />

          <span className="custom-checkbox">
            <i className="ri-check-line"></i>
          </span>

          <span>Generate Office Copy</span>
        </label>

        <span className="copy-help">
          {printBothCopies ? "Student + Office copy" : "Student copy"}
        </span>
      </div>

      {/* =====================================================
          PRINT AREA
      ===================================================== */}

      <div id="PrintDiv" className="hostel-receipt-print-area">
        <ReceiptCopy copyType="Student" />

        {printBothCopies && (
          <>
            <div className="hostel-copy-separator no-print">
              <span>OFFICE COPY</span>
            </div>

            <ReceiptCopy copyType="Office" />
          </>
        )}
      </div>
    </div>
  );
}
